import _ from 'lodash'

import type { IncomeStream } from 'renter/interfaces/api/income'
import { filterSuppressedFraudSignals } from 'shared/config/fraudIndicators'
import { printDollarsFromCents } from 'shared/utils/dollar-print'

export const renderAmountCell = (value?: number | null): string =>
  value !== null && value !== undefined ? printDollarsFromCents(value) : 'N/A'

const ELEVATED_FRAUD_RISK_LEVELS = new Set(['MEDIUM', 'HIGH', 'VERY_HIGH'])

export const ocrResultHasFraud = (ocrResult: any): boolean => {
  if (!ocrResult) return false
  if (ocrResult.hasFraudSignal) return true
  return ELEVATED_FRAUD_RISK_LEVELS.has(_.get(ocrResult, 'fraudRiskLevel'))
}

export const ocrResultHasVisibleFraud = (ocrResult: any): boolean => {
  if (!ocrResult) return false
  const rawSignals = _.get(ocrResult, 'fraudSignals') || []
  if (
    rawSignals.length > 0 &&
    filterSuppressedFraudSignals(rawSignals).length === 0
  ) {
    return false
  }
  return ocrResultHasFraud(ocrResult)
}

// Adaptive Decisioning reason for the fraud card (PRODUCT-1568).
//
// `adaptiveDecisionRawResponse` is only persisted when the adaptive path won
// on this submission, so its absence already covers both "customer not on AD"
// and "AD fell back to the legacy raw score" — a missing reason line is the
// intended "AD fell back" tell.
//
// Resistant returns exactly one operator-facing string, at
// `reason.sub_reason.label`; there is no sibling reason label. `value` is the
// internal rule id (e.g. MULTIPLE_MANIPULATION_DETECTION) and is deliberately
// not surfaced. Keys are camelCased by the axios interceptor before they
// reach us.
export const getAdaptiveDecisionReason = (ocrResult: any): string | null => {
  if (!ocrResult) return null
  if (_.get(ocrResult, 'fraudRiskLevel') === 'LOW') return null
  const label = _.get(
    ocrResult,
    'adaptiveDecisionRawResponse.reason.subReason.label'
  )
  if (typeof label !== 'string') return null
  const trimmed = label.trim()
  return trimmed.length > 0 ? trimmed : null
}

export const incomeIsFlaggedForFraud = (income: any): boolean => {
  if (!income) return false
  if (income.isFraudulent) return true
  return ELEVATED_FRAUD_RISK_LEVELS.has(_.get(income, 'fraudRiskLevel'))
}

export const streamHasFraud = (incomeStream: IncomeStream): boolean =>
  _.some(incomeStream?.incomes, incomeIsFlaggedForFraud)

interface DetectSingleDocumentStreamParams {
  ocrResults: any[]
  incomesCount: number
  canSeeFraud: boolean
}

export const detectSingleDocumentStream = ({
  ocrResults,
  incomesCount,
  canSeeFraud,
}: DetectSingleDocumentStreamParams): boolean => {
  if (!canSeeFraud) return false
  if (incomesCount <= 1) return false
  if (ocrResults.length !== incomesCount) return false
  const ocrGroups = _.map(ocrResults, 'ocrGroup')
  if (_.some(ocrGroups, (g) => !g)) return false
  if (_.uniq(ocrGroups).length !== 1) return false
  const fraudStates = _.uniq(_.map(ocrResults, ocrResultHasFraud))
  return fraudStates.length <= 1
}

interface ConversionAffordanceParams {
  isManagerOverride: boolean
  convertedAmount?: number | null
  streamQualifies: boolean
}

export const shouldCaptionAsConverted = ({
  isManagerOverride,
  convertedAmount,
  streamQualifies,
}: ConversionAffordanceParams): boolean => {
  if (isManagerOverride) return false
  if (convertedAmount === null || convertedAmount === undefined) return false
  return streamQualifies
}

// Matches income-service isEmptyAccountMask: blank, all-'x' (XXXX) or
// non-alphanumeric (****) masks don't identify a real account.
const isPlaceholderAccountMask = (mask: string): boolean => {
  const normalized = mask.toLowerCase()
  return normalized === '' || /^x+$/.test(normalized) || /^[^a-z0-9]+$/.test(normalized)
}

// Same "<name> (*<mask>)" shape as the Verified Balances account column, so a
// manager can match a stream to its account and owner.
export const formatStreamBankAccount = (
  stream?: Pick<IncomeStream, 'accountInstitution' | 'accountMask'> | null
): string | null => {
  const institution = _.trim(stream?.accountInstitution ?? '')
  const rawMask = _.trim(stream?.accountMask ?? '')
  const mask = isPlaceholderAccountMask(rawMask) ? '' : rawMask

  if (institution && mask) return `${institution} (*${mask})`
  if (institution) return institution
  if (mask) return `*${mask}`
  return null
}
